/**
 * RichEditor.tsx — Shared rich text editor + MediaPickerModal
 * Reused in: BlogEditor, ProductEditor, PageEditor
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import {
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Image, Code, Quote, Heading1, Heading2, Heading3,
  Eye, EyeOff, Library, X, Upload, CheckCircle2,
} from 'lucide-react';

function execCmd(cmd: string, value?: string) {
  document.execCommand(cmd, false, value);
}

// ─── Media Picker Modal ───────────────────────────────────────────────────────
interface MediaFile { name: string; url: string; type: string; original?: string; }
interface MediaPickerProps { onSelect: (url: string) => void; onClose: () => void; }

export function MediaPickerModal({ onSelect, onClose }: MediaPickerProps) {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'library' | 'upload'>('library');
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const loadFiles = () => {
    fetch('/php-backend/cms-api/media.php')
      .then(r => r.json()).then(d => setFiles(d.files || []))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadFiles(); }, []);

  const doUpload = async (file: File) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch('/php-backend/cms-api/media.php', { method: 'POST', body: fd });
      const d = await r.json();
      if (d.file) { setFiles(f => [d.file, ...f]); setTab('library'); }
    } finally { setUploading(false); }
  };

  const images = files.filter(f =>
    f.type?.startsWith('image') || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(f.name)
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.78)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0a0c12] border border-white/15 rounded-2xl w-full max-w-3xl max-h-[82vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Library size={16} /> Librărie Media — Alege imagine
          </h3>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors"><X size={18} /></button>
        </div>
        <div className="flex gap-1 px-5 pt-3">
          {(['library', 'upload'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${t === tab ? 'bg-[#295CA8] text-white' : 'text-white/40 hover:text-white border border-white/10'}`}>
              {t === 'library' ? '🖼 Librărie' : '⬆ Upload'}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-auto p-5">
          {tab === 'upload' ? (
            <div className="border-2 border-dashed border-white/15 rounded-xl p-10 text-center hover:border-white/30 transition-colors cursor-pointer"
              onClick={() => fileRef.current?.click()}>
              <input ref={fileRef} type="file" accept="image/*,video/*,audio/*" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) doUpload(f); }} />
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin w-8 h-8 border-2 border-[#4ab8ff] border-t-transparent rounded-full" />
                  <p className="text-sm text-white/50">Se optimizează și salvează...</p>
                </div>
              ) : (
                <>
                  <Upload size={28} className="mx-auto mb-2 text-white/20" />
                  <p className="text-sm text-white/60">Click sau drag & drop</p>
                  <p className="text-xs text-white/25 mt-1">JPG, PNG → WebP automat · SVG, GIF păstrate</p>
                </>
              )}
            </div>
          ) : loading ? (
            <div className="text-center py-8 text-white/30 text-sm">Se încarcă...</div>
          ) : images.length === 0 ? (
            <div className="text-center py-10 text-white/25">
              <Library size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Librăria este goală — încarcă prima imagine!</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {images.map((f, i) => (
                <button key={f.name + i} onClick={() => onSelect(f.url)}
                  className="group aspect-square rounded-xl overflow-hidden border border-white/10 hover:border-[#4ab8ff] transition-all relative bg-white/5">
                  <img src={f.url} alt={f.original || f.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-[#295CA8]/75 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="text-center">
                      <CheckCircle2 size={20} className="mx-auto text-white mb-1" />
                      <span className="text-white text-[10px] font-medium">Selectează</span>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-[9px] text-white/70 truncate">{f.original || f.name}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Toolbar Button ───────────────────────────────────────────────────────────
function ToolbarBtn({ icon, title, onClick, active }: {
  icon: React.ReactNode; title: string; onClick: () => void; active?: boolean;
}) {
  return (
    <button type="button" title={title} onMouseDown={e => { e.preventDefault(); onClick(); }}
      className={`p-1.5 rounded text-sm transition-colors ${active ? 'bg-[#295CA8] text-white' : 'text-white/50 hover:text-white hover:bg-white/10'}`}>
      {icon}
    </button>
  );
}

// ─── Rich Text Editor ─────────────────────────────────────────────────────────
interface RichEditorProps {
  value: string;
  onChange: (v: string) => void;
  onInsertImageFromLibrary?: () => void;
  minHeight?: number;
  placeholder?: string;
}

export function RichEditor({ value, onChange, onInsertImageFromLibrary, minHeight = 280, placeholder }: RichEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<'visual' | 'html'>('visual');
  const [htmlValue, setHtmlValue] = useState(value);
  const isInit = useRef(false);

  // Init editor content
  useEffect(() => {
    if (editorRef.current && !isInit.current) {
      editorRef.current.innerHTML = value || '';
      isInit.current = true;
    }
  }, []);

  // Sync if value changes externally (language switch etc.)
  useEffect(() => {
    if (editorRef.current && isInit.current && viewMode === 'visual') {
      const current = editorRef.current.innerHTML;
      if (current !== value && value !== htmlValue) {
        editorRef.current.innerHTML = value || '';
        setHtmlValue(value || '');
      }
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      setHtmlValue(html);
      onChange(html);
    }
  }, [onChange]);

  const cmd = (command: string, val?: string) => {
    editorRef.current?.focus();
    execCmd(command, val);
    handleInput();
  };

  const insertHtml = (html: string) => {
    editorRef.current?.focus();
    document.execCommand('insertHTML', false, html);
    handleInput();
  };

  // ── Auto-localize external images on paste ────────────────────────────────
  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData.getData('text/html');
    if (!html) return; // plain text paste — let default happen
    
    // Check for external img tags
    const extImgRegex = /<img[^>]+src=["'](https?:\/\/[^"']+)["'][^>]*>/gi;
    const externalImgs = [...html.matchAll(extImgRegex)];
    if (externalImgs.length === 0) return; // no external images, let default paste

    e.preventDefault();
    let processedHtml = html;

    for (const match of externalImgs) {
      const extUrl = match[1];
      // Skip if already local
      if (extUrl.includes(window.location.host) || extUrl.startsWith('/')) continue;
      try {
        const r = await fetch('/php-backend/cms-api/download-external.php', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: extUrl }),
        });
        const d = await r.json();
        if (d.url) {
          processedHtml = processedHtml.replace(extUrl, d.url);
        }
      } catch { /* keep original on error */ }
    }
    // Clean and insert
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = processedHtml;
    const cleanHtml = tempDiv.innerHTML;
    document.execCommand('insertHTML', false, cleanHtml);
    handleInput();
  }, [handleInput]);

  const insertLink = () => {
    const url = prompt('URL link:');
    if (url) cmd('createLink', url);
  };

  const insertImageByUrl = async () => {
    const url = prompt('URL imagine (extern → salvat automat în librărie):');
    if (!url) return;
    if (url.startsWith('/php-backend/') || url.startsWith('/uploads/')) {
      insertHtml(`<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:10px 0;" />`);
      return;
    }
    try {
      const r = await fetch('/php-backend/cms-api/download-external.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const d = await r.json();
      const finalUrl = d.url || url;
      insertHtml(`<img src="${finalUrl}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:10px 0;" />`);
    } catch {
      insertHtml(`<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:10px 0;" />`);
    }
  };

  const switchToHtml = () => {
    if (editorRef.current) setHtmlValue(editorRef.current.innerHTML);
    setViewMode('html');
  };

  const switchToVisual = () => {
    if (editorRef.current && htmlValue) {
      editorRef.current.innerHTML = htmlValue;
      onChange(htmlValue);
    }
    setViewMode('visual');
  };

  const toolbarGroups = [
    [
      { icon: <Bold size={13} />,      title: 'Bold (Ctrl+B)',    action: () => cmd('bold') },
      { icon: <Italic size={13} />,    title: 'Italic (Ctrl+I)',  action: () => cmd('italic') },
      { icon: <Underline size={13} />, title: 'Subliniat',        action: () => cmd('underline') },
    ],
    [
      { icon: <Heading1 size={13} />, title: 'Titlu H1', action: () => cmd('formatBlock', 'H1') },
      { icon: <Heading2 size={13} />, title: 'Titlu H2', action: () => cmd('formatBlock', 'H2') },
      { icon: <Heading3 size={13} />, title: 'Titlu H3', action: () => cmd('formatBlock', 'H3') },
    ],
    [
      { icon: <AlignLeft size={13} />,   title: 'Stânga',  action: () => cmd('justifyLeft') },
      { icon: <AlignCenter size={13} />, title: 'Centrat', action: () => cmd('justifyCenter') },
      { icon: <AlignRight size={13} />,  title: 'Dreapta', action: () => cmd('justifyRight') },
    ],
    [
      { icon: <List size={13} />,        title: 'Listă puncte',    action: () => cmd('insertUnorderedList') },
      { icon: <ListOrdered size={13} />, title: 'Listă numerică', action: () => cmd('insertOrderedList') },
      { icon: <Quote size={13} />,       title: 'Citat',          action: () => cmd('formatBlock', 'BLOCKQUOTE') },
    ],
    [
      { icon: <LinkIcon size={13} />, title: 'Inserează link',           action: insertLink },
      { icon: <Image size={13} />,    title: 'Imagine (URL / extern)',    action: insertImageByUrl },
      { icon: <Library size={13} />, title: 'Librărie Media',           action: () => onInsertImageFromLibrary?.() },
      { icon: <Code size={13} />,     title: 'Bloc cod',                 action: () => cmd('formatBlock', 'PRE') },
    ],
  ];

  const charCount = htmlValue.replace(/<[^>]*>/g, '').length;

  return (
    <div className="border border-white/15 rounded-xl overflow-hidden" style={{ background: '#070910' }}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-white/10 flex-wrap" style={{ background: 'rgba(255,255,255,0.015)' }}>
        {toolbarGroups.map((group, gi) => (
          <div key={gi} className="flex items-center gap-0.5 pr-1.5 border-r border-white/10 last:border-0">
            {group.map((btn, bi) => <ToolbarBtn key={bi} icon={btn.icon} title={btn.title} onClick={btn.action} />)}
          </div>
        ))}
        <div className="ml-auto">
          <button type="button" onMouseDown={e => e.preventDefault()}
            onClick={viewMode === 'visual' ? switchToHtml : switchToVisual}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-all border ${
              viewMode === 'html'
                ? 'bg-[#295CA8]/20 border-[#295CA8]/50 text-[#4ab8ff]'
                : 'border-white/10 text-white/35 hover:text-white'
            }`}>
            {viewMode === 'html' ? <><Eye size={10} /> Visual</> : <><EyeOff size={10} /> HTML</>}
          </button>
        </div>
      </div>

      {/* Editor */}
      {viewMode === 'visual' ? (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          className="p-4 text-white/85 text-sm leading-relaxed focus:outline-none"
          style={{ minHeight, fontFamily: 'inherit' }}
          data-placeholder={placeholder || 'Scrieți conținut...'}
        />
      ) : (
        <textarea
          value={htmlValue}
          onChange={e => { setHtmlValue(e.target.value); onChange(e.target.value); }}
          className="w-full p-4 bg-transparent text-[#4ab8ff] text-xs font-mono focus:outline-none resize-y"
          style={{ minHeight, lineHeight: 1.6 }}
          placeholder="<p>HTML direct...</p>"
        />
      )}

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-1.5 border-t border-white/5" style={{ background: 'rgba(255,255,255,0.008)' }}>
        <span className="text-[10px] text-white/20">
          {viewMode === 'visual' ? 'Editor vizual — imagini externe sunt salvate automat local' : 'Mod HTML direct'}
        </span>
        <span className="text-[10px] text-white/20">{charCount} car.</span>
      </div>
    </div>
  );
}
